import { HugeiconsIcon } from '@hugeicons/react';
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  Loading02Icon,
  SecurityCheckIcon,
  UserIcon,
} from '@hugeicons/core-free-icons';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { api, apiError } from '@/lib/api';
import { PageHeader, RiskBadge } from '@/components/common/Bits';
import { UserPicker } from '@/components/common/UserPicker';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const SCALE = [
  { v: 1, label: 'Muy bajo' },
  { v: 2, label: 'Bajo' },
  { v: 3, label: 'Moderado' },
  { v: 4, label: 'Alto' },
  { v: 5, label: 'Crítico' },
];

function levelFromScore(s) {
  if (s < 30) return 'Bajo';
  if (s < 45) return 'Medio';
  if (s < 60) return 'Medio-Alto';
  if (s < 80) return 'Alto';
  return 'Crítico';
}

// Compute which review flags are triggered (mirrors backend logic)
function computeFlags(answers) {
  const inherent = Object.values(answers).length
    ? Math.round((Object.values(answers).reduce((a, b) => a + b, 0) / Object.values(answers).length) * 20)
    : 0;
  return {
    dpo: (answers.privacidad || 0) >= 4 || (answers.cumplimiento || 0) >= 4,
    clinical: (answers.impacto_clinico || 0) >= 3,
    security: (answers.seguridad || 0) >= 4 || (answers.autonomia || 0) >= 4,
    committee: inherent >= 45,
  };
}

const REVIEW_META = {
  dpo: { label: 'Revisión DPO (Protección de Datos)', description: 'Privacidad ≥ 4 o Cumplimiento ≥ 4' },
  clinical: { label: 'Revisión Clínica', description: 'Impacto clínico ≥ 3' },
  security: { label: 'Revisión de Seguridad', description: 'Seguridad ≥ 4 o Autonomía ≥ 4' },
  committee: { label: 'Revisión del Comité de IA', description: 'Riesgo inherente ≥ 45' },
};

export default function AssessmentRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reevalId = params.get('reeval');
  const [catalogs, setCatalogs] = useState(null);
  const [ini, setIni] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('dimensions'); // 'dimensions' | 'reviewers'
  const [reviewers, setReviewers] = useState({});

  useEffect(() => {
    api.get('/catalogs').then(({ data }) => {
      setCatalogs(data);
      setAnswers(Object.fromEntries(data.dimensions.map((d) => [d.key, 2])));
    });
    api.get(`/initiatives/${id}`).then(({ data }) => setIni(data.initiative));
  }, [id]);

  const preview = useMemo(() => {
    const vals = Object.values(answers);
    if (!vals.length) return null;
    const inherent = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 20);
    return { inherent, level: levelFromScore(inherent) };
  }, [answers]);

  const flags = useMemo(() => computeFlags(answers), [answers]);
  const activeFlags = useMemo(
    () =>
      Object.entries(flags)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [flags],
  );

  const goToReviewers = () => {
    if (activeFlags.length === 0) {
      submit();
    } else {
      setStep('reviewers');
    }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const reviewersPayload = {};
      for (const [flag, user] of Object.entries(reviewers)) {
        if (user && user.id) {
          reviewersPayload[flag] = { user_id: user.id, name: user.name, email: user.email };
        }
      }

      const { data } = await api.post(`/initiatives/${id}/assessments`, {
        answers,
        notes,
        reviewers: reviewersPayload,
        reevaluation_id: reevalId,
      });
      toast.success(
        `Evaluación v${data.version} registrada · riesgo ${data.inherent_level} / residual ${data.residual_level}`,
      );
      navigate(`/iniciativas/${id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!catalogs || !ini) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground -ml-2 mb-4">
        <HugeiconsIcon icon={ArrowLeft01Icon} className="h-4 w-4 mr-1" /> Volver
      </Button>
      <PageHeader
        title={`Risk Assessment · ${ini.name}`}
        subtitle={
          step === 'dimensions'
            ? reevalId
              ? 'Reevaluación en curso: se generará una nueva versión conservando la anterior.'
              : 'Cuestionario de 15 dimensiones. El sistema calcula riesgo inherente, residual, controles obligatorios y ruta de aprobación.'
            : 'Asigne las personas responsables de cada revisión requerida. Se les notificará automáticamente.'
        }
        testId="assessment-header"
      />

      {/* Step indicator */}
      <div className="flex items-center gap-3 mb-6" aria-label="Pasos del assessment">
        <button
          onClick={() => setStep('dimensions')}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
            step === 'dimensions' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
          )}>
          <span className="h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-semibold">
            1
          </span>
          Dimensiones de riesgo
        </button>
        <div className="h-px w-6 bg-border" />
        <button
          onClick={() => activeFlags.length > 0 && setStep('reviewers')}
          disabled={activeFlags.length === 0}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
            step === 'reviewers' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
            activeFlags.length === 0 && 'opacity-50 cursor-not-allowed',
          )}>
          <span
            className={cn(
              'h-5 w-5 rounded-full text-[10px] flex items-center justify-center font-semibold',
              step === 'reviewers' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}>
            2
          </span>
          Asignar revisores
          {activeFlags.length > 0 && (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0">
              {activeFlags.length}
            </Badge>
          )}
        </button>
      </div>

      {step === 'dimensions' && (
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-3">
            {catalogs.dimensions.map((dim, idx) => (
              <Card key={dim.key} className="shadow-card border-border">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold">
                        {idx + 1}. {dim.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{dim.question}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5 mt-3" data-testid={`dimension-${dim.key}`}>
                    {SCALE.map((s) => (
                      <button
                        key={s.v}
                        onClick={() => setAnswers((p) => ({ ...p, [dim.key]: s.v }))}
                        data-testid={`dim-${dim.key}-${s.v}`}
                        className={cn(
                          'rounded-lg border px-1 py-2 text-[11px] font-medium transition-colors',
                          answers[dim.key] === s.v
                            ? s.v >= 4
                              ? 'border-risk-high bg-risk-high/10 text-risk-high'
                              : s.v === 3
                                ? 'border-risk-medium bg-risk-medium/10 text-risk-medium'
                                : 'border-accent bg-accent/10 text-accent'
                            : 'border-border bg-card text-muted-foreground hover:border-primary/40',
                        )}>
                        {s.v} · {s.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            <Card className="shadow-card border-border">
              <CardContent className="p-5 space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">Notas del evaluador</Label>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Observaciones, supuestos y evidencias consideradas"
                  data-testid="assessment-notes"
                />
              </CardContent>
            </Card>
          </div>

          <div className="lg:sticky lg:top-24 space-y-4">
            <Card className="shadow-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-primary/8 text-primary flex items-center justify-center">
                    <HugeiconsIcon icon={SecurityCheckIcon} className="h-4 w-4" />
                  </div>
                  <p className="font-display font-semibold text-sm">Resultado preliminar</p>
                </div>
                {preview && (
                  <>
                    <div className="flex items-end justify-between">
                      <span className="font-display text-4xl font-semibold tabular">{preview.inherent}</span>
                      <RiskBadge level={preview.level} />
                    </div>
                    <Progress value={preview.inherent} className="h-2 mt-3" />
                  </>
                )}

                {/* Flags preview */}
                {activeFlags.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border space-y-1.5">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                      Revisiones requeridas
                    </p>
                    {activeFlags.map((flag) => (
                      <div key={flag} className="flex items-center gap-1.5 text-xs">
                        <HugeiconsIcon icon={CheckmarkCircle02Icon} className="h-3 w-3 text-accent" />
                        <span>{REVIEW_META[flag].label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  onClick={goToReviewers}
                  disabled={saving}
                  className="w-full mt-5 bg-accent hover:bg-accent/90 text-accent-foreground"
                  data-testid="assessment-next-btn">
                  {activeFlags.length > 0 ? (
                    <>
                      Asignar revisores <HugeiconsIcon icon={ArrowRight01Icon} className="h-4 w-4 ml-1.5" />
                    </>
                  ) : saving ? (
                    <HugeiconsIcon icon={Loading02Icon} className="h-4 w-4 animate-spin" />
                  ) : (
                    'Registrar evaluación'
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {step === 'reviewers' && (
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 space-y-4">
            <p className="text-sm text-muted-foreground">
              Según las respuestas, se requieren <strong>{activeFlags.length}</strong> revisiones antes de avanzar.
              Seleccione quién debe realizar cada una. El sistema les notificará automáticamente.
            </p>

            {activeFlags.map((flag) => (
              <Card key={flag} className="shadow-card border-border">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-xl bg-primary/8 text-primary flex items-center justify-center shrink-0">
                      <HugeiconsIcon icon={UserIcon} className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{REVIEW_META[flag].label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{REVIEW_META[flag].description}</p>
                      <div className="mt-3">
                        <Label htmlFor={`reviewer-${flag}`} className="text-xs text-muted-foreground mb-1.5 block">
                          Responsable asignado
                        </Label>
                        <UserPicker
                          value={reviewers[flag]?.name || ''}
                          onSelect={(user) => setReviewers((prev) => ({ ...prev, [flag]: user }))}
                          onClear={() =>
                            setReviewers((prev) => {
                              const n = { ...prev };
                              delete n[flag];
                              return n;
                            })
                          }
                          placeholder="Buscar por nombre o correo..."
                          testId={`reviewer-picker-${flag}`}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="lg:sticky lg:top-24">
            <Card className="shadow-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-8 w-8 rounded-lg bg-primary/8 text-primary flex items-center justify-center">
                    <HugeiconsIcon icon={SecurityCheckIcon} className="h-4 w-4" />
                  </div>
                  <p className="font-display font-semibold text-sm">Resumen</p>
                </div>
                {preview && (
                  <div className="flex items-end justify-between mb-3">
                    <span className="font-display text-3xl font-semibold tabular">{preview.inherent}</span>
                    <RiskBadge level={preview.level} />
                  </div>
                )}
                <div className="space-y-2 text-xs">
                  {activeFlags.map((flag) => (
                    <div key={flag} className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground truncate">{REVIEW_META[flag].label.split(' (')[0]}</span>
                      {reviewers[flag] ? (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-accent/10 text-accent border-accent/25 shrink-0">
                          {reviewers[flag].name.split(' ')[0]}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground shrink-0">
                          Sin asignar
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-4 leading-relaxed">
                  Las revisiones sin asignar se enviarán al primer usuario disponible con el rol correspondiente.
                </p>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" onClick={() => setStep('dimensions')} className="flex-1 border-border">
                    Volver
                  </Button>
                  <Button
                    onClick={submit}
                    disabled={saving}
                    className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
                    data-testid="assessment-submit-btn">
                    {saving ? <HugeiconsIcon icon={Loading02Icon} className="h-4 w-4 animate-spin" /> : 'Registrar'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
